import MarkdownIt from 'markdown-it';
import {
  defaultMarkdownSerializer,
  MarkdownParser,
  MarkdownSerializer,
  type MarkdownSerializerState,
  type ParseSpec,
} from 'prosemirror-markdown';
import {
  DOMSerializer,
  Fragment,
  Slice,
  type Mark,
  type Node as ProseMirrorNode,
} from 'prosemirror-model';

import type { OutgoingMentions } from '#lib/core/client.svelte.js';
import type { ImageSourcePackReferenceView } from '#src/generated/protocol';

import { mfmUnixtime, parseMfmColor, parseMfmUnixtime, utcFallbackLabel } from '../time-markup';
import { mfmPlugin } from './mfm';
import { isMscLink, linkMscs } from './msc-links';
import { codeLanguage, composerSchema, parseMatrixHtml, ROOM_PING } from './schema';
import { docToMarkdown } from './to-markdown';

export interface ComposerMessage {
  body: string;
  formatted: string | null;
  mentions: OutgoingMentions;
  imageSourcePacks?: ImageSourcePackReferenceView[];
}

type AutolinkState = MarkdownSerializerState & { inAutolink?: boolean };
const SPOILER_FALLBACK = '\uE000';

function isBareUrl(mark: Mark, parent: ProseMirrorNode, index: number): boolean {
  const child = parent.child(index);
  if (!child.isText || child.marks.length !== 1) return false;
  if (index + 1 < parent.childCount && mark.isInSet(parent.child(index + 1).marks)) return false;

  const href = mark.attrs.href as string;
  const text = child.text ?? '';
  return (
    text === href ||
    `https://${text}` === href ||
    `mailto:${text}` === href ||
    isMscLink(text, href)
  );
}

function cellLine(row: ProseMirrorNode): string {
  const cells: string[] = [];
  row.forEach((cell) => {
    const text = cell.textBetween(0, cell.content.size, ' ', (node) =>
      node.type === composerSchema.nodes.hard_break ? ' ' : atomText(node)
    );
    cells.push(text.replaceAll('|', '\\|').trim());
  });
  return `| ${cells.join(' | ')} |`;
}

function renderContent(state: MarkdownSerializerState, node: ProseMirrorNode): void {
  state.renderContent(node);
}

function boldBlock(state: MarkdownSerializerState, node: ProseMirrorNode): void {
  state.write('**');
  state.renderInline(node);
  state.write('**');
  state.closeBlock(node);
}

const markdown = new MarkdownSerializer(
  {
    ...defaultMarkdownSerializer.nodes,
    text: (state, node) => {
      state.text(node.text ?? '', false);
    },
    /* The default writes markdown's `\\` hard-break escape, which shows up as
       a stray backslash in clients that only render the plain body. */
    hard_break: (state, node, parent, index) => {
      for (let i = index + 1; i < parent.childCount; i += 1) {
        if (parent.child(i).type !== node.type) {
          state.write('\n');
          return;
        }
      }
    },
    code_block: (state, node) => {
      state.text(docToMarkdown(composerSchema.topNodeType.create(null, node)), false);
      state.closeBlock(node);
    },
    horizontal_rule: (state, node) => {
      state.write('---');
      state.closeBlock(node);
    },
    subtext: (state, node) => {
      state.write('-# ');
      state.renderInline(node, false);
      state.closeBlock(node);
    },
    details: renderContent,
    summary: boldBlock,
    description_list: renderContent,
    description_term: boldBlock,
    description_details: renderContent,
    table: (state, node) => {
      node.forEach((row, _offset, index) => {
        state.write(cellLine(row));
        state.ensureNewLine();
        if (index !== 0) return;
        state.write(`|${' --- |'.repeat(row.childCount)}`);
        state.ensureNewLine();
      });
      state.closeBlock(node);
    },
    table_row: () => undefined,
    table_cell: () => undefined,
    table_header: () => undefined,
    math_block: (state, node) => {
      state.write(`$$\n${node.attrs.latex as string}\n$$`);
      state.closeBlock(node);
    },
    math_inline: (state, node) => {
      state.text(`$${node.attrs.latex as string}$`, false);
    },
    room_ping: (state) => {
      state.text(ROOM_PING, false);
    },
    mention: (state, node) => {
      state.text(node.attrs.name as string, false);
    },
    mfm_time: (state, node) => {
      state.text(mfmUnixtime(node.attrs.datetime as string), false);
    },
    emoticon: (state, node) => {
      state.text(`:${node.attrs.shortcode as string}:`, false);
    },
  },
  {
    ...defaultMarkdownSerializer.marks,
    strike: { open: '~~', close: '~~', mixable: true, expelEnclosingWhitespace: true },
    underline: { open: '', close: '', mixable: true },
    sub: { open: '', close: '', mixable: true },
    sup: { open: '', close: '', mixable: true },
    color: {
      open: (_state, mark) => {
        return `$[fg.color=${(mark.attrs.value as string).slice(1)} `;
      },
      close: ']',
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    bg_color: {
      open: (_state, mark) => {
        return `$[bg.color=${(mark.attrs.value as string).slice(1)} `;
      },
      close: ']',
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    spoiler: { open: '||', close: '||', mixable: true, expelEnclosingWhitespace: true },
    link: {
      open: (state, mark, parent, index) => {
        const bare = isBareUrl(mark, parent, index);
        (state as AutolinkState).inAutolink = bare;
        return bare ? '' : '[';
      },
      close: (state, mark, parent, index) => {
        const bare = (state as AutolinkState).inAutolink ?? isBareUrl(mark, parent, index);
        (state as AutolinkState).inAutolink = undefined;
        return bare ? '' : `](${(mark.attrs.href as string).replaceAll(/[()"]/g, '\\$&')})`;
      },
      mixable: true,
    },
  }
);

function withoutTrailingParagraph(doc: ProseMirrorNode): ProseMirrorNode {
  const kept: ProseMirrorNode[] = [];
  doc.forEach((child) => kept.push(child));
  while (kept.length > 1) {
    const last = kept.at(-1);
    if (last?.type !== composerSchema.nodes.paragraph || last.content.size > 0) break;
    kept.pop();
  }

  return kept.length === doc.childCount ? doc : doc.copy(Fragment.fromArray(kept));
}

function trimmedText(node: ProseMirrorNode, trim: (text: string) => string): ProseMirrorNode[] {
  const text = trim(node.text ?? '');
  return text === '' ? [] : [composerSchema.text(text, node.marks)];
}

const DROPPED_WHEN_BLANK = new Set([
  'code_block',
  'blockquote',
  'bullet_list',
  'ordered_list',
  'list_item',
]);

function blank(node: ProseMirrorNode): boolean {
  if (node.isLeaf) return false;
  if (node.isTextblock) return node.content.size === 0;
  let empty = true;
  node.forEach((child) => {
    empty &&= blank(child);
  });
  return empty;
}

function normalizedBlocks(node: ProseMirrorNode): ProseMirrorNode {
  if (node.isLeaf || node.type === composerSchema.nodes.code_block) return node;

  const children: ProseMirrorNode[] = [];
  node.forEach((child) => {
    if (child.isText && child.text?.includes('\n')) {
      child.text.split('\n').forEach((line, index) => {
        if (index > 0) children.push(composerSchema.nodes.hard_break.create());
        if (line !== '') children.push(composerSchema.text(line, child.marks));
      });
      return;
    }
    const normalized = normalizedBlocks(child);
    if (!DROPPED_WHEN_BLANK.has(normalized.type.name) || !blank(normalized)) {
      children.push(normalized);
    }
  });
  if (node.isTextblock) {
    for (;;) {
      const last = children.at(-1);
      if (last?.type === composerSchema.nodes.hard_break) children.pop();
      else if (last?.isText && last.text?.trimEnd() !== last.text)
        children.splice(-1, 1, ...trimmedText(last, (text) => text.trimEnd()));
      else break;
    }
    for (;;) {
      const first = children.at(0);
      if (first?.type === composerSchema.nodes.hard_break) children.shift();
      else if (first?.isText && first.text?.trimStart() !== first.text)
        children.splice(0, 1, ...trimmedText(first, (text) => text.trimStart()));
      else break;
    }
  }
  return node.copy(Fragment.fromArray(children));
}

function flattenRoomPings(node: ProseMirrorNode): ProseMirrorNode {
  if (node.isLeaf) return node;

  const children: ProseMirrorNode[] = [];
  node.forEach((child) => {
    children.push(
      child.type === composerSchema.nodes.room_ping
        ? composerSchema.text(ROOM_PING)
        : flattenRoomPings(child)
    );
  });

  return node.copy(Fragment.fromArray(children));
}

function isPlain(doc: ProseMirrorNode): boolean {
  const { paragraph, hard_break: hardBreak } = composerSchema.nodes;
  let plain = true;

  doc.descendants((node) => {
    if (!plain) return false;
    if (node.marks.length > 0) plain = false;
    else if (node.type !== paragraph && node.type !== hardBreak && !node.isText) plain = false;
    return plain;
  });

  return plain;
}

function mfmMarks(node: ProseMirrorNode, fg: string | null, bg: string | null): readonly Mark[] {
  let marks = node.marks;
  if (fg) marks = composerSchema.marks.color.create({ value: fg }).addToSet(marks);
  if (bg) marks = composerSchema.marks.bg_color.create({ value: bg }).addToSet(marks);
  return marks;
}

function expandedMfmText(node: ProseMirrorNode): ProseMirrorNode[] {
  const text = node.text ?? '';
  const children: ProseMirrorNode[] = [];
  let plainStart = 0;
  let index = 0;

  const pushText = (value: string, marks = node.marks): void => {
    if (value !== '') children.push(composerSchema.text(value, marks));
  };

  while (index < text.length) {
    const rest = text.slice(index);
    if (rest.startsWith('\\')) {
      const escaped = rest.slice(1).match(/^./u)?.[0] ?? '';
      index += 1 + escaped.length;
      continue;
    }

    const unix = parseMfmUnixtime(rest);
    if (unix) {
      pushText(text.slice(plainStart, index));
      children.push(
        composerSchema.nodes.mfm_time.create(
          { datetime: unix.datetime, label: utcFallbackLabel(unix.datetime) },
          null,
          node.marks
        )
      );
      index += unix.raw.length;
      plainStart = index;
      continue;
    }

    const color = parseMfmColor(rest);
    if (color) {
      pushText(text.slice(plainStart, index));
      const colored = composerSchema.text(color.text, mfmMarks(node, color.args.fg, color.args.bg));
      children.push(...expandedMfmText(colored));
      index += color.raw.length;
      plainStart = index;
      continue;
    }

    index += rest.match(/^./u)?.[0].length ?? 1;
  }

  if (children.length === 0) return [node];
  pushText(text.slice(plainStart));
  return children;
}

function expandMfm(node: ProseMirrorNode): ProseMirrorNode {
  if (node.isLeaf || node.type.spec.code) return node;

  const children: ProseMirrorNode[] = [];
  node.forEach((child) => {
    if (child.isText && !composerSchema.marks.code.isInSet(child.marks)) {
      children.push(...expandedMfmText(child));
    } else {
      children.push(expandMfm(child));
    }
  });
  return node.copy(Fragment.fromArray(children));
}

function html(doc: ProseMirrorNode): string {
  const holder = document.createElement('div');
  holder.append(DOMSerializer.fromSchema(composerSchema).serializeFragment(doc.content));
  for (const paragraph of holder.querySelectorAll('li > p:only-child, dd > p:only-child')) {
    paragraph.replaceWith(...paragraph.childNodes);
  }
  for (const anchor of holder.querySelectorAll('a[data-org\\.matrix\\.msc4550\\.link]')) {
    if (anchor.textContent === anchor.getAttribute('href')) {
      anchor.removeAttribute('data-org.matrix.msc4550.link');
    }
  }

  const spaced = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
  for (let node = spaced.nextNode(); node; node = spaced.nextNode()) {
    if (node instanceof Text && !node.parentElement?.closest('pre')) {
      node.data = node.data.replaceAll(/ (?= )/g, '\u00A0');
    }
  }

  const texts = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
  const literalMfm: { node: Text; offset: number }[] = [];
  for (let node = texts.nextNode(); node; node = texts.nextNode()) {
    if (!(node instanceof Text) || node.parentElement?.closest('code, pre, a, time')) continue;
    for (
      let offset = node.data.indexOf('$[');
      offset >= 0;
      offset = node.data.indexOf('$[', offset + 2)
    ) {
      const source = node.data.slice(offset);
      if (parseMfmUnixtime(source) || parseMfmColor(source)) literalMfm.push({ node, offset });
    }
  }
  for (const { node, offset } of literalMfm.reverse()) {
    const suffix = node.splitText(offset);
    suffix.deleteData(0, 1);
    const dollar = document.createElement('span');
    dollar.textContent = '$';
    suffix.before(dollar);
  }

  const protectedTexts: Text[] = [];
  const walker = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node instanceof Text && !node.parentElement?.closest('code, pre, a, time')) {
      protectedTexts.push(node);
    }
  }
  for (const node of protectedTexts) {
    if (!receiverFormats(node.data)) continue;
    const pieces = node.data.split(/([*_~`|])/u);
    node.replaceWith(
      ...pieces.map((piece, index) => {
        if (index % 2 === 0) return document.createTextNode(piece);
        const literal = document.createElement('span');
        literal.textContent = piece;
        return literal;
      })
    );
  }

  const blocks = Array.from(holder.children);
  if (blocks.length === 1 && blocks[0]?.tagName === 'P') return blocks[0].innerHTML;
  return blocks.map((block) => block.outerHTML).join('');
}

export function composerMarkdown(doc: ProseMirrorNode): string {
  return docToMarkdown(withoutTrailingParagraph(flattenRoomPings(doc)));
}

function spoilerFallback(node: ProseMirrorNode): ProseMirrorNode {
  if (node.isText && composerSchema.marks.spoiler.isInSet(node.marks)) {
    return composerSchema.text(
      SPOILER_FALLBACK,
      node.marks.filter((mark) => mark.type !== composerSchema.marks.spoiler)
    );
  }

  if (node.isLeaf) return node;

  const children: ProseMirrorNode[] = [];
  let previousWasSpoiler = false;
  node.forEach((child) => {
    const isSpoiler = child.isText && Boolean(composerSchema.marks.spoiler.isInSet(child.marks));
    if (!isSpoiler || !previousWasSpoiler) children.push(spoilerFallback(child));
    previousWasSpoiler = isSpoiler;
  });
  return node.copy(Fragment.from(children));
}

function imageSourcePacksOf(doc: ProseMirrorNode): ImageSourcePackReferenceView[] {
  const references: ImageSourcePackReferenceView[] = [];
  doc.descendants((node) => {
    if (node.type !== composerSchema.nodes.emoticon) return;
    const source = node.attrs.sourcePack as unknown;
    if (
      typeof node.attrs.url !== 'string' ||
      source === null ||
      typeof source !== 'object' ||
      !('room_id' in source) ||
      !('state_key' in source) ||
      !('shortcode' in source) ||
      !('via' in source) ||
      typeof source.room_id !== 'string' ||
      typeof source.state_key !== 'string' ||
      typeof source.shortcode !== 'string' ||
      !Array.isArray(source.via) ||
      !source.via.every((server) => typeof server === 'string')
    )
      return;
    references.push({
      url: node.attrs.url,
      source: {
        room_id: source.room_id,
        state_key: source.state_key,
        shortcode: source.shortcode,
        via: source.via,
      },
    });
  });
  return references;
}

export function serializeComposer(doc: ProseMirrorNode): ComposerMessage {
  const mentions = mentionsOf(doc);
  const source = withoutTrailingParagraph(normalizedBlocks(flattenRoomPings(doc)));
  const sourceWasPlain = isPlain(source);
  const flat = expandMfm(source);
  const linked = linkMscs(flat);
  const imageSourcePacks = imageSourcePacksOf(flat);
  const plainBody = plainTextOf(source).trim();
  if (isPlain(linked) && !receiverFormats(plainBody)) {
    return {
      body: plainBody,
      formatted: null,
      mentions,
      ...(imageSourcePacks.length > 0 && { imageSourcePacks }),
    };
  }

  const body = sourceWasPlain
    ? plainTextOf(source).trim()
    : markdown.serialize(spoilerFallback(flat)).replaceAll(SPOILER_FALLBACK, '[Spoiler]').trim();
  if (body === '')
    return {
      body,
      formatted: null,
      mentions,
      ...(imageSourcePacks.length > 0 && { imageSourcePacks }),
    };

  return {
    body,
    formatted: html(linked),
    mentions,
    ...(imageSourcePacks.length > 0 && { imageSourcePacks }),
  };
}

function mentionsRoom(doc: ProseMirrorNode): boolean {
  let found = false;

  doc.descendants((node) => {
    if (found) return false;
    if (!node.isTextblock) return true;
    if (node.type === composerSchema.nodes.code_block) return false;

    let text = '';
    node.forEach((child) => {
      if (child.type === composerSchema.nodes.room_ping) {
        text += ROOM_PING;
        return;
      }
      const quoted = !child.isText || composerSchema.marks.code.isInSet(child.marks);
      text += quoted ? ' ' : (child.text ?? '');
    });
    if (/(^|\s)@room(\s|$)/.test(text)) found = true;
    return false;
  });

  return found;
}

export function mentionsOf(doc: ProseMirrorNode): OutgoingMentions {
  const userIds = new Set<string>();

  doc.descendants((node) => {
    if (node.type !== composerSchema.nodes.mention) return true;
    const userId = node.attrs.userId as string;
    if (userId.startsWith('@')) userIds.add(userId);
    return false;
  });

  return { userIds: [...userIds], room: mentionsRoom(doc) };
}

const tokenizer = MarkdownIt('commonmark', { html: false })
  .enable(['strikethrough', 'table'])
  .use(mfmPlugin);

const RECEIVER_FORMATS = new Set([
  'em_open',
  'strong_open',
  's_open',
  'code_inline',
  'spoiler_open',
]);

const quoteRules = new MarkdownIt('commonmark').block.ruler;
quoteRules.enableOnly('blockquote');
const [parseBlockquote] = quoteRules.getRules('');
tokenizer.block.ruler.at(
  'blockquote',
  (state, startLine, endLine, silent) => {
    const start = state.bMarks[startLine] + state.tShift[startLine];
    if (!/^>[ \t]/.test(state.src.slice(start, state.eMarks[startLine]))) return false;
    return parseBlockquote(state, startLine, endLine, silent);
  },
  { alt: ['paragraph', 'reference', 'blockquote'] }
);

function receiverFormats(text: string): boolean {
  return tokenizer
    .parseInline(text, {})
    .some((token) => token.children?.some((child) => RECEIVER_FORMATS.has(child.type)));
}

tokenizer.block.ruler.before(
  'heading',
  'subtext',
  (state, startLine, _endLine, silent) => {
    if (state.sCount[startLine] - state.blkIndent >= 4) return false;
    const start = state.bMarks[startLine] + state.tShift[startLine];
    const max = state.eMarks[startLine];
    const match = /^-#[ \t]+(\S.*)$/.exec(state.src.slice(start, max));
    if (!match) return false;
    if (silent) return true;

    state.line = startLine + 1;
    state.push('subtext_open', 'sub', 1);
    const inline = state.push('inline', '', 0);
    inline.content = match[1].trim();
    inline.children = [];
    state.push('subtext_close', 'sub', -1);
    return true;
  },
  { alt: ['paragraph', 'reference', 'blockquote'] }
);

const TEXT_STOPS = new Set(Array.from('\n!#$%&*+-:<=>@[\\]^_`{}~|', (char) => char.charCodeAt(0)));

tokenizer.inline.ruler.at('text', (state, silent) => {
  let pos = state.pos;
  while (pos < state.posMax && !TEXT_STOPS.has(state.src.charCodeAt(pos))) pos++;
  if (pos === state.pos) return false;
  if (!silent) state.pending += state.src.slice(state.pos, pos);
  state.pos = pos;
  return true;
});

function spoilerClose(src: string, from: number, max: number): number {
  for (let index = from; index + 2 <= max; index += 1) {
    if (src[index] === '\\') index += 1;
    else if (src.startsWith('||', index)) return index;
  }
  return -1;
}

tokenizer.inline.ruler.before('text', 'spoiler', (state, silent) => {
  const start = state.pos;
  if (!state.src.startsWith('||', start)) return false;
  const close = spoilerClose(state.src, start + 2, state.posMax);
  if (close < 0) return false;
  const inner = state.src.slice(start + 2, close);
  if (inner === '' || /^\s|\s$/.test(inner)) return false;

  if (!silent) {
    const max = state.posMax;
    state.pos = start + 2;
    state.posMax = close;
    state.push('spoiler_open', 'span', 1);
    state.md.inline.tokenize(state);
    state.push('spoiler_close', 'span', -1);
    state.posMax = max;
  }
  state.pos = close + 2;
  return true;
});

const PARSE_TOKENS: Record<string, ParseSpec> = {
  paragraph: { block: 'paragraph' },
  blockquote: { block: 'blockquote' },
  list_item: { block: 'list_item' },
  bullet_list: { block: 'bullet_list' },
  ordered_list: {
    block: 'ordered_list',
    getAttrs: (token) => ({ order: Number(token.attrGet('start') ?? '1') || 1 }),
  },
  heading: {
    block: 'heading',
    getAttrs: (token) => ({ level: Math.min(6, Number(token.tag.slice(1))) }),
  },
  subtext: { block: 'subtext' },
  code_block: { block: 'code_block', noCloseToken: true },
  fence: {
    block: 'code_block',
    noCloseToken: true,
    getAttrs: (token) => ({ language: codeLanguage(token.info.trim().split(/\s+/)[0] ?? '') }),
  },
  hardbreak: { node: 'hard_break' },
  softbreak: { node: 'hard_break' },
  em: { mark: 'em' },
  strong: { mark: 'strong' },
  s: { mark: 'strike' },
  spoiler: { mark: 'spoiler' },
  code_inline: { mark: 'code', noCloseToken: true },
  link: { mark: 'link', getAttrs: (token) => ({ href: token.attrGet('href') ?? '' }) },
  hr: { node: 'horizontal_rule' },
  mfm_time: {
    node: 'mfm_time',
    getAttrs: (token) => ({ datetime: token.content, label: utcFallbackLabel(token.content) }),
  },
  mfm_fg: {
    mark: 'color',
    getAttrs: (token) => ({ value: token.attrGet('value') ?? '' }),
  },
  mfm_bg: {
    mark: 'bg_color',
    getAttrs: (token) => ({ value: token.attrGet('value') ?? '' }),
  },
  image: {
    node: 'image',
    getAttrs: (token) => ({
      src: token.attrGet('src') ?? '',
      alt: token.children?.map((child) => child.content).join('') ?? '',
      title: token.attrGet('title') ?? '',
    }),
  },
  table: { block: 'table' },
  thead: { ignore: true },
  tbody: { ignore: true },
  tr: { block: 'table_row' },
  th: { block: 'table_header' },
  td: { block: 'table_cell' },
};

const markdownParser = new MarkdownParser(
  composerSchema,
  tokenizer as unknown as ConstructorParameters<typeof MarkdownParser>[1],
  PARSE_TOKENS
);

const ATOM_PLACEHOLDER = '\uFFFC';

function parseMarkdown(source: string): ProseMirrorNode {
  return markdownParser.parse(source.replaceAll('¯\\_(ツ)_/¯', '¯\\\\\\_(ツ)\\_/¯'));
}

export function atomText(node: ProseMirrorNode): string {
  const { emoticon, room_ping: roomPing, image, math_inline: math } = composerSchema.nodes;
  if (node.type === emoticon) return `:${node.attrs.shortcode as string}:`;
  if (node.type === roomPing) return ROOM_PING;
  if (node.type === image) return (node.attrs.alt as string) || (node.attrs.src as string);
  if (node.type === composerSchema.nodes.mfm_time)
    return mfmUnixtime(node.attrs.datetime as string);
  if (node.type === math) return `$${node.attrs.latex as string}$`;
  if (node.type === composerSchema.nodes.math_block) return `$$${node.attrs.latex as string}$$`;
  return (node.attrs.name as string | undefined) ?? '';
}

const PLACEHOLDER_ATOMS = new Set([
  composerSchema.nodes.mfm_time,
  composerSchema.nodes.mention,
  composerSchema.nodes.emoticon,
  composerSchema.nodes.room_ping,
  composerSchema.nodes.math_inline,
  composerSchema.nodes.image,
]);

/** The literal characters the user typed, with the atoms spelled back out. */
export function plainTextOf(doc: ProseMirrorNode): string {
  const { hard_break: hardBreak } = composerSchema.nodes;

  return doc.textBetween(0, doc.content.size, '\n\n', (node) =>
    node.type === hardBreak ? '\n' : atomText(node)
  );
}

export function commandTextOf(doc: ProseMirrorNode): string {
  const fenceCodeBlocks = (node: ProseMirrorNode): ProseMirrorNode => {
    if (node.type === composerSchema.nodes.code_block) {
      const fenced = docToMarkdown(composerSchema.topNodeType.create(null, node));
      return node.copy(Fragment.from(composerSchema.text(fenced)));
    }
    if (node.isLeaf) return node;
    return node.copy(Fragment.fromArray(node.children.map(fenceCodeBlocks)));
  };

  return plainTextOf(fenceCodeBlocks(doc));
}

function markdownSourceOf(doc: ProseMirrorNode): { source: string; atoms: ProseMirrorNode[] } {
  const { hard_break: hardBreak } = composerSchema.nodes;
  const atoms: ProseMirrorNode[] = [];

  const source = doc.textBetween(0, doc.content.size, '\n\n', (node) => {
    if (node.type === hardBreak) return '\n';
    if (!PLACEHOLDER_ATOMS.has(node.type)) return '';
    atoms.push(node);
    return ATOM_PLACEHOLDER;
  });

  return { source, atoms };
}

function spliceAtoms(node: ProseMirrorNode, atoms: ProseMirrorNode[]): ProseMirrorNode {
  if (node.isLeaf) return node;

  const children: ProseMirrorNode[] = [];
  node.forEach((child) => {
    if (!child.isText) {
      children.push(spliceAtoms(child, atoms));
      return;
    }

    for (const [index, part] of (child.text ?? '').split(ATOM_PLACEHOLDER).entries()) {
      if (index > 0) {
        const atom = atoms.shift();
        if (atom) children.push(node.type.spec.code ? composerSchema.text(atomText(atom)) : atom);
      }
      if (part !== '') children.push(composerSchema.text(part, child.marks));
    }
  });

  return node.copy(Fragment.fromArray(children));
}

/** Plain-text mode: what was typed is the body, parsed as markdown for the HTML. */
export function serializePlain(doc: ProseMirrorNode): ComposerMessage {
  const body = plainTextOf(doc).replaceAll('\u00a0', ' ').trim();
  const mentions = mentionsOf(doc);
  const imageSourcePacks = imageSourcePacksOf(doc);
  if (body === '')
    return {
      body,
      formatted: null,
      mentions,
      ...(imageSourcePacks.length > 0 && { imageSourcePacks }),
    };

  const { source, atoms } = markdownSourceOf(doc);
  const parsed = linkMscs(
    withoutTrailingParagraph(
      flattenRoomPings(spliceAtoms(parseMarkdown(source.replaceAll('\u00a0', ' ').trim()), atoms))
    )
  );
  return {
    body,
    formatted:
      isPlain(parsed) && plainTextOf(parsed) === body && !receiverFormats(body)
        ? null
        : html(parsed),
    mentions,
    ...(imageSourcePacks.length > 0 && { imageSourcePacks }),
  };
}

export function plainEditDoc(body: string, html: string): ProseMirrorNode {
  const sent = serializePlain(textDoc(body)).formatted;
  const target = parseMatrixHtml(html);
  if (sent !== null && parseMatrixHtml(sent).eq(target)) return textDoc(body);

  const atoms: ProseMirrorNode[] = [];
  function replaceAtoms(node: ProseMirrorNode): ProseMirrorNode {
    if (node.type === composerSchema.nodes.mention || node.type === composerSchema.nodes.emoticon) {
      atoms.push(node);
      return composerSchema.text(ATOM_PLACEHOLDER, node.marks);
    }
    if (node.isLeaf) return node;
    return node.copy(Fragment.fromArray(node.children.map(replaceAtoms)));
  }

  const source = composerMarkdown(replaceAtoms(target));
  return spliceAtoms(textDoc(source), atoms);
}

export function richFromPlain(doc: ProseMirrorNode): ProseMirrorNode {
  const { source, atoms } = markdownSourceOf(doc);
  return spliceAtoms(parseMarkdown(source.trim()), atoms);
}

export function markdownSlice(text: string): Slice {
  const parsed = parseMarkdown(text);
  const only = parsed.childCount === 1 ? parsed.firstChild : null;
  if (only?.type === composerSchema.nodes.paragraph) return new Slice(only.content, 0, 0);
  return new Slice(parsed.content, 0, 0);
}

function textBlocks(text: string): ProseMirrorNode[] {
  return text
    .replaceAll(/\r\n?/g, '\n')
    .split('\n\n')
    .map((block) => {
      const content: ProseMirrorNode[] = [];
      for (const [index, line] of block.split('\n').entries()) {
        if (index > 0) content.push(composerSchema.nodes.hard_break.create());
        if (line !== '') content.push(composerSchema.text(line));
      }
      return composerSchema.nodes.paragraph.create(null, content);
    });
}

export function textDoc(text: string): ProseMirrorNode {
  return composerSchema.topNodeType.create(null, Fragment.fromArray(textBlocks(text)));
}

export function textSlice(text: string): Slice {
  const blocks = textBlocks(text);

  const only = blocks.length === 1 ? blocks[0] : null;
  if (only) return new Slice(only.content, 0, 0);
  return new Slice(Fragment.fromArray(blocks), 1, 1);
}

export function markdownFromSlice(slice: Slice): string {
  const inline = slice.content.firstChild?.isInline ?? false;
  const content = inline
    ? Fragment.from(composerSchema.nodes.paragraph.create(null, slice.content))
    : slice.content;

  const doc = flattenRoomPings(composerSchema.topNodeType.create(null, content));
  return isPlain(doc) ? plainTextOf(doc) : docToMarkdown(doc);
}
